import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import { Browser } from '@capacitor/browser';
import { Observable } from 'rxjs';
import { EnodeService } from 'src/app/services/enode/enode.service';
import {
  EnodeVehicle,
  LinkResponse,
  UserLocation
} from 'src/app/services/enode/enode.types';
import { AddLocationModalComponent } from '../add-location-modal/add-location-modal.component';
import LinkUI from 'src/app';

@Component({
  selector: 'app-admin-menu-modal',
  templateUrl: './admin-menu-modal.component.html',
  styleUrls: ['./admin-menu-modal.component.scss']
})
export class AdminMenuModalComponent {
  @Input() locations: UserLocation[];
  @Input() vehicles: EnodeVehicle[];
  @Input() selectedVehicleId: string;
  @Output() locationsChanged = new EventEmitter<UserLocation[]>();

  linkVehicle$: Observable<LinkResponse>;
  isLoading = false;
  locationAddresses: (UserLocation & { address: string })[];
  modalPage: HTMLIonModalElement;
  showChargingLocationId: string;
  showSmartChargeInfo: boolean = false;
  showUnlinkModal: boolean = false;
  selectedVehicle: EnodeVehicle;

  constructor(
    private modalCtrl: ModalController,
    private _enodeService: EnodeService,
    public platform: Platform
  ) {
    this.linkVehicle$ = this._enodeService.linkVehicle();
  }

  async ngOnInit() {
    this.selectedVehicle = this.vehicles.find(
      (v) => v.id === this.selectedVehicleId
    );
    if (this.locations) {
      const res = this.locations.map(async (l) => ({
        ...l,
        address: await this.getAddress(l.latitude, l.longitude)
      }));
      this.locationAddresses = await Promise.all(res);
    }
  }

  cancel() {
    this.modalCtrl.dismiss({
      locations: this.locations
    });
  }

  linkVehicle() {
    this.linkVehicle$.subscribe({
      next: async (res) => {
        this.isLoading = true;
        if (this.platform.is('capacitor')) {
          LinkUI.show({ token: res.linkToken }).then((res) => {
            const { code, errMsg } = res;
            switch (code) {
              case 'success':
                setTimeout(() => {
                  this.isLoading = false;
                }, 60000);
                break;
              case 'cancelledByUser':
                this.isLoading = false;
                break;
              default:
                this.isLoading = false;
                break;
            }
          });
        } else {
          await Browser.open({
            url: res.linkUrl
          });
        }
        setTimeout(() => {
          this.isLoading = false;
          this.modalCtrl.dismiss();
        }, 30000);
      }
    });
  }

  selectVehicle(vehicle: EnodeVehicle) {
    this.selectedVehicleId = vehicle.id;
    this.selectedVehicle = vehicle;
    this.modalCtrl.dismiss({
      selectedVehicleId: this.selectedVehicleId
    });
  }

  async getAddress(lat: number, lng: number): Promise<string> {
    if (lat && lng) {
      const geocoder = new google.maps.Geocoder();
      const res = await geocoder.geocode({
        location: { lat: lat, lng: lng }
      });
      const place = res.results.find((result) => {
        if (result.types.includes('street_address')) {
          return result;
        } else if (result.types.includes('establishment')) {
          return result;
        }
      });
      return place?.formatted_address;
    }
  }

  async showLocationModal() {
    this.modalPage = await this.modalCtrl.create({
      component: AddLocationModalComponent,
      cssClass: 'add-location-modal-content',
      backdropDismiss: true
    });
    this.modalPage.onDidDismiss().then(async (data) => {
      if (data?.data?.location) {
        const newAddress = await this.getAddress(
          data.data.location.latitude,
          data.data.location.longitude
        );
        this.locationAddresses.push({
          ...data.data.location,
          address: newAddress
        });
        this.locations.push(data.data.location);
        this.locationsChanged.emit(this.locations);
      }
    });
    return await this.modalPage.present();
  }

  deleteLocation(locationId: string) {
    this._enodeService.deleteLocation(locationId).subscribe({
      next: () => {
        this.locationAddresses = this.locationAddresses.filter(
          (l) => l.id !== locationId
        );
        this.locations = this.locations.filter((l) => l.id !== locationId);
        this.locationsChanged.emit(this.locations);
      }
    });
  }

  showChargingLocationModal(location: UserLocation) {
    this.showChargingLocationId = location.id;
  }

  openUnlikModal() {
    this.showUnlinkModal = true;
  }
  canDismissUnlink(e: any) {
    const { data } = e.detail;
    if (data === undefined) {
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  }
}
